/* Core & libraries */
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch } from "../endpoints/base";
import { GET_DATA_SHARING, GET_TOKEN_VERSA_LINK, SEND_PROVIDERS_TO_VERSA_LINK } from "../endpoints/endpoints";
import { useUserContext } from "../contexts/userContext";
import { useNotificationContext } from "../contexts/notificationContext";

/* Components */
/* Styles */

// services/integrationService.js

export const useIntegrationService = () => {
  const { genericApiFetch } = useGenericApiFetch();
  const { setNotification } = useNotificationContext();

  const [integrationDataSharing, setIntegrationDataSharing] = useState({});
  const [dutyOfCareDataSharing, setDutyOfCareDataSharing] = useState({});

  const userDetails = useUserContext();
  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;

  const getDataSharing = async (integrationType) => {
    const response = await genericApiFetch({
      url: GET_DATA_SHARING.replace("{companyEntityId}", companyEntityId),
      queryParams: { integrationType },
      method: "GET",
    });

    if (response.success) {
      if (integrationType === "ExpenseIntegration") {
        setIntegrationDataSharing(response.data);
      } else {
        setDutyOfCareDataSharing(response.data);
      }
      return true;
    }
    return false;
  };

  const setProgramDetails = async (programDetailsData, spinner, integrationType) => {
    const response = await genericApiFetch({
      url: GET_DATA_SHARING.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data: programDetailsData,
      queryParams: { integrationType },
      spinner,
    });

    if (response.success) return true;

    if (response?.errorMessages?.[0]?.code === "10006") {
      setNotification(
        "error",
        <FormattedMessage id="expensify.error.unableToConnect.title" />,
        <FormattedMessage id="expensify.error.unableToConnect.description" />,
        true,
      );
    } else {
      setNotification(
        "error",
        <FormattedMessage id="expensify.error.title" />,
        <FormattedMessage id="expensify.error.description" />,
        true,
      );
    }
    return false;
  };

  const getVersaLinkToken = async () => {
    const response = await genericApiFetch({
      url: GET_TOKEN_VERSA_LINK.replace("{companyEntityId}", companyEntityId),
      method: "GET",
      spinner: "versaLinkSpinner",
    });

    if (!response?.success || !response?.data) {
      setNotification(
        "error",
        <FormattedMessage id="versaLink.error.unableToStart.title" />,
        <FormattedMessage id="versaLink.error.unableToStart.description" />,
        true,
      );
      return null;
    }

    const data = response.data;

    if (data.statusCode !== "SUCCESS" || data.apiResponseStatus !== "SUCCESS" || !data.token) {
      setNotification(
        "error",
        <FormattedMessage id="versaLink.error.unableToStart.title" />,
        <FormattedMessage id="versaLink.error.unableToStart.description" />,
        true,
      );
      return null;
    }

    return {
      token: data.token,
      mode: data.mode,
      expired: data.expired,
      whitelistOrgs: data.whitelistOrgs,
    };
  };

  const sendProvidersToVersaLink = async (payload) => {
    const response = await genericApiFetch({
      url: SEND_PROVIDERS_TO_VERSA_LINK.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data: payload,
      spinner: "versaLinkSpinner",
    });

    if (!response?.success) {
      setNotification(
        "error",
        <FormattedMessage id="versaLink.error.syncProviders.title" />,
        <FormattedMessage id="versaLink.error.syncProviders.description" />,
        true,
      );
      return null;
    }

    return response.data ?? true;
  };

  return {
    getDataSharing,
    setProgramDetails,
    integrationDataSharing,
    dutyOfCareDataSharing,
    setIntegrationDataSharing,
    getVersaLinkToken,
    sendProvidersToVersaLink
  };
};